<script setup lang="ts">
import {WifiOff, X, AlertTriangle, MessageSquare, PanelRight, PanelRightClose} from 'lucide-vue-next'
import {ResizeHandle} from '~/components/ui/resize-handle'
import AppBar from '~/components/shell/AppBar.vue'
import IconRail from '~/components/shell/IconRail.vue'
import FileExplorer from '~/components/documents/FileExplorer.vue'
// The WEB app's chat panel. This used to be the only thing keeping the right component on
// screen: the Word add-in's panel was `components/ChatPanel.vue`, which claimed the same bare
// auto-import name, and a root-level file beats a nested one — so deleting this line silently
// mounted the add-in's panel here. The add-in's is `WordChatPanel.vue` now, `ChatPanel`
// unambiguously names this file, and the import below is belt-and-braces rather than the only
// thing standing between the web app and the wrong component.
import ChatPanel from '~/components/chat/ChatPanel.vue'
import PlanFeatureNotice from '~/components/plans/PlanFeatureNotice.vue'
import {blockedFeatureFor} from '~/utils/planGatedRoutes'
import {planAllows} from '~/composables/usePlanFeature'
import ContextRail from '~/blades/ContextRail.vue'
// Explicit: Nuxt's composables auto-import only scans the top level of app/composables, so anything
// under composables/web-chat/ has to be imported by path.
import {useRestoreScope} from '~/composables/web-chat/useRestoreScope'
// Explicit, though `useChatDock` itself is auto-imported: the popup's height is the same fact as
// its corners (#351), so the value the browser lays out is built from the constants the unit tests
// pin rather than spelled out a second time as an arbitrary Tailwind value.
import {PANEL_HEIGHT_CSS, PANEL_WIDTH_CSS} from '~/composables/useChatDock'
import MobileTabBar from '~/components/shell/MobileTabBar.vue'
import GuidanceNoticeBanner from '~/components/guidance/GuidanceNoticeBanner.vue'
import {storeToRefs} from 'pinia'
import {useDocumentsStore} from '~/stores/documents'
import {useActivityStore} from '~/stores/activity'
import UploadTargetDialog from '~/components/documents/UploadTargetDialog.vue'
import {usageLimitMessageKey} from '#shared/usage/usageLimit'
const {t} = useI18n()
const route = useRoute()
const {degraded} = useServiceStatus()
const {blocked: usageBlocked, info: usageInfo, dismiss: dismissUsageLimit} = useUsageLimit()
const {
  docsDrawerOpen,
  sidebarWidth,
  isSidebarCollapsed,
  isSidebarPeeking,
  isSidebarResizing,
  chatPanelOpen,
  chatCorner,
  contextRailExpanded,
  toggleChatPanel,
  toggleContextRail,
  startSidebarPeek,
  scheduleDismissSidebarPeek,
  clearPeekSuppressionIfOutside,
  loadState,
} = usePanelLayout()
const webChat = useWebChatStore()
const activity = useActivityStore()
const contextRailAvailable = computed(() => webChat.activeScope.kind !== 'global')

function onShellPointerMove(e: MouseEvent | PointerEvent) {
  if ((e.buttons ?? 0) === 0) {
    clearPeekSuppressionIfOutside(e.clientX)
  }
  if (isSidebarCollapsed.value && !isSidebarPeeking.value && e.clientX <= 10) {
    startSidebarPeek()
  } else if (isSidebarCollapsed.value && isSidebarPeeking.value && e.clientX > sidebarWidth.value + 16) {
    scheduleDismissSidebarPeek()
  }
}

const {ensure: ensureProfile, profile} = useUserProfile()
const documents = useDocumentsStore()
const casesStore = useCasesStore()
const clientsStore = useClientsStore()
const {isFolderMode} = storeToRefs(documents)
const {bootstrap, fetchGroups, loadPreferences: loadBrowserState} = documents
// The single poller behind the Activity Center (MAR-109). Mounted here, alongside the panel it feeds,
// so it covers every /app route: work started anywhere stays visible in the bell, and its one fetch on
// mount rehydrates anything still in flight from before a reload.
useActivityFeed()
// Restore the remembered chat scope for the session. Here rather than in the composer, so the scope
// (and the context rail it drives) is correct on browsing pages too — see `useRestoreScope`.
useRestoreScope()
const bannerDismissed = ref(false)

// Content is active when something needs the center area (pushes chat to right panel).
// Below `sm` only one of the three columns is on screen at a time; `mobilePane` says which.
const { pane: mobilePane, contentActive, reset: resetMobilePane } = useMobilePane()
const isHome = computed(() => !contentActive.value)

/**
 * How the chat column presents itself (MAR-125).
 *
 * Two shapes, one instance:
 *  - no page   → it IS the centre column, flexing to fill the light region
 *  - a page    → desktop: a floating popup anchored to the panel's bottom-right, over the content
 *                mobile:  still a full-width pane, because the tab bar already gives it the screen
 *
 * The popup is positioned against the light-region panel (which is `relative`), not the viewport, so
 * it stays inside the shell's rounded inset instead of sitting on top of the rails.
 */
// The floating chat is draggable between the four corners of the light region; the corner is
// persisted panel state. See `useChatDock` for why it snaps rather than free-floating.
const lightRegionEl = ref<HTMLElement | null>(null)
const chatPanelEl = ref<HTMLElement | null>(null)
const {dragging: chatDragging, dragStyle: chatDragStyle, cornerClass: chatCornerClass,
  onPointerDown: onChatPointerDown, onPointerMove: onChatPointerMove, onPointerUp: onChatPointerUp,
  onClickCapture: onChatClickCapture}
  = useChatDock(lightRegionEl, chatPanelEl)

// The FAB docks the same way the panel does — it is the same chat, just collapsed, so being able to
// move the panel but not the button would be an odd seam. `selfHandle` because the FAB has no header
// to grab: the button IS the handle, which is also why the drag needs a threshold to keep the click.
const chatFabEl = ref<HTMLElement | null>(null)
const {dragging: fabDragging, dragStyle: fabDragStyle, consumeDrag: fabConsumeDrag,
  onPointerDown: onFabPointerDown, onPointerMove: onFabPointerMove, onPointerUp: onFabPointerUp}
  = useChatDock(lightRegionEl, chatFabEl, {selfHandle: true})

/**
 * The chat is a plan feature (MAR-1212): without a seat (`app`) or on a plan without `chat`, the
 * server refuses every chat request. The chat home and a conversation have no page of their own, so
 * the plan notice takes the chat's place in the centre; over a content page the floating chat and its
 * FAB are simply not offered (the page shows its own notice when it is blocked too). Judged as the
 * chat home, `'/app'`, because that is where the chat's own gate lives. An unloaded profile keeps the
 * chat, as before: a notice would be a guess, and the server still refuses.
 */
const chatBlockedFeature = computed(() => profile.value ? blockedFeatureFor('/app', profile.value.feature_flags) : null)

function onFabClick() {
  // A press that turned into a drag must not also open the chat.
  if (fabConsumeDrag()) return
  toggleChatPanel()
}

const chatContainerClass = computed(() => contentActive.value
  ? [
      'w-full', // below `sm` it is an in-flow pane; the `sm:` rules below make it float
      'sm:absolute sm:z-40',
      chatCornerClass.value,
      // Height comes from `--chat-panel-h` (bound below, built from `useChatDock`'s constants)
      // rather than an arbitrary value, because it is load-bearing for the DOCK: a panel taller
      // than the room it has to move in turns four corners into two, and parks its drag band over
      // the content column's first row of controls (#351). Width comes from `--chat-panel-w` for the
      // same single-source reason: a page that keeps its controls clear of the open panel (the
      // settings shell, MAR-933 §15) reserves `PANEL_CLEARANCE_CSS`, which is built from it.
      'sm:w-(--chat-panel-w) sm:h-(--chat-panel-h)',
      'sm:rounded-2xl sm:border sm:border-border sm:shadow-2xl sm:bg-background sm:overflow-hidden',
      // Animate the snap, but never the drag itself — transitioning during a pointer-move makes the
      // panel lag the cursor.
      chatDragging.value ? 'select-none' : 'sm:transition-[top,left,right,bottom] sm:duration-150',
    ]
  : ['flex-1 min-w-0'])

// `--chat-panel-h` is declared at every width but only CONSUMED by the `sm:` height utility above,
// so the below-`sm` in-flow pane keeps its auto height. The drag's inline placement is spread last:
// while dragging it must win, and it sets `top`/`left`, never `height`.
const chatPanelStyle = computed(() => ({
  '--chat-panel-h': PANEL_HEIGHT_CSS,
  '--chat-panel-w': isSidebarCollapsed.value
    ? PANEL_WIDTH_CSS
    : `min(${PANEL_WIDTH_CSS}, max(280px, calc(34% - 20px)))`,
  ...(chatDragStyle.value ?? {}),
}))

// ── NO GLOBAL DROP ZONE (#83, 2026-09-05) ──────────────────────────────────────────────────────
//
// This component used to register window-level `dragover`/`dragleave`/`drop` listeners plus a
// backdrop overlay, so a file dropped ANYWHERE in the shell was accepted. The comment that stood
// here argued for "accept, then ask": the bytes went up into `currentPath` and `UploadTargetDialog`
// opened afterwards to pick a client, a matter or a conversation, on the grounds that a drop is a
// gesture the user has already made and refusing it reads as broken.
//
// **That decision is reversed.** The cost of it was a drop with no target at drop time — the
// catch-all this ticket exists to abolish — and "ask afterwards" is answerable with a dismissed
// dialog, which puts the document straight back on the unattached pile. Dropping now works ONLY
// where the surface itself names the target: the chat box (`ChatPanel`), a document list or the
// documents sidebar (`useFolderDrop`), a case card (`useCaseDocuments`), a client's Documentos
// rollup (`useClientDocuments`) and a browser group header (`BrowserGroupedList`). Each of those
// owns its own `drop` handler now; there is no window handler left for a zone to delegate to, and
// re-adding one is what would bring the pile back.
//
// Reaching a drop target that is off-screen when the drag begins (#84) is a separate mechanism and
// survives: `useDragAutoScroll` owns its own window listeners in the capture phase, never calls
// `preventDefault`, and is mounted here once for the whole `/app` shell.
useDragAutoScroll()

let unregisterAfterEach: (() => void) | null = null
let autoCollapsedForNarrowViewport = false

function onViewportResize() {
  if (typeof window === 'undefined') return
  if (window.innerWidth < 768) {
    if (!isSidebarCollapsed.value) {
      autoCollapsedForNarrowViewport = true
      isSidebarCollapsed.value = true
      isSidebarPeeking.value = false
    }
  } else if (autoCollapsedForNarrowViewport) {
    autoCollapsedForNarrowViewport = false
    isSidebarCollapsed.value = false
  }
}

onMounted(async () => {
  loadState()
  onViewportResize()
  window.addEventListener('resize', onViewportResize)
  loadBrowserState()
  // Clear the mobile pane override after every navigation, so the pane falls back to what the route
  // implies: tap Documentos, then tap Clientes, and you get Clientes rather than the documents pane
  // sitting over a page you asked for. A router hook rather than a `watch` — the repo prefers
  // handlers, and `afterEach` returns its own unregister function.
  unregisterAfterEach = useRouter().afterEach(() => resetMobilePane())

  // A member without a seat is refused every document read (`403 no_seat`, MAR-1212) and sees the
  // license notice instead of the app: the browser's loads are not asked at all.
  if (!(await planAllows('app'))) return

  // Single request for items + folders (was two separate fetches).
  bootstrap()
  // Grouped mode needs three things, because a group is DERIVED rather than fetched (MAR-125): the
  // documents, the cases that own them, and — in project mode — the clients those cases belong to.
  // Loading cases here is what makes a case created anywhere in the app appear in the sidebar
  // immediately: the header is the `Case` object, so there is nothing left to invalidate.
  if (!isFolderMode.value) {
    fetchGroups()
    // Both stores skip the request on a plan without case management (MAR-980). The catch is for
    // everything else: a sidebar grouping that failed to load is not worth an unhandled rejection on
    // every page — the groups simply stay underived until the next load.
    casesStore.listCases().catch(err => console.warn('[PanelLayout] cases load failed:', err))
    clientsStore.listClients().catch(err => console.warn('[PanelLayout] clients load failed:', err))
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', onViewportResize)
  }
  unregisterAfterEach?.()
  unregisterAfterEach = null
})

// Lock document scroll while the app shell is mounted so the page can't overscroll past the layout on
// mobile (only the internal panels scroll). useHead ties the class to this component's lifecycle, so it
// is removed automatically when navigating to a marketing route (where PanelLayout unmounts).
useHead({htmlAttrs: {class: 'app-shell-lock'}})
</script>

<template>
  <!-- `h-full`, not `h-viewport`: the layout wrapper in `layouts/default.vue` owns the viewport
       height so the superadmin override banner can sit above this in flow instead of covering it. -->
  <!-- `bg-shell-0` is the page backdrop BEHIND the frame — the same value as the frame in each theme
       (MAR-1316). It is covered by the frame row in a normal viewport; it exists so an overscroll or
       a sub-pixel gap exposes shell rather than paper. -->
  <!-- SAFE-AREA INSETS, three of the four. `viewport-fit=cover` (nuxt.config.ts) moves the layout
       origin to the PHYSICAL screen edges so MobileTabBar can carry `bg-sidebar` under the Android
       gesture bar. That opt-in is global, so it also exposes the top and the sides: the manifest
       declares `display: standalone`, and installed on a notched device the status bar would sit
       over the app bar's controls and, in landscape, the cutout over the IconRail. These pad the
       shell back off those edges, and `bg-shell-0` is what fills the gap — the right colour, since
       it is the ramp rung that sits behind the frame anyway.
       The BOTTOM is deliberately absent: MobileTabBar owns it with its own
       `pb-[env(safe-area-inset-bottom)]`, and padding here too would double the inset and leave a
       shell-coloured band under the bar. Each inset is 0 wherever the platform has no cutout — in a
       browser tab the toolbar owns that space — so this costs nothing off a notched standalone. -->
  <div
      class="h-full min-h-0 overflow-hidden flex flex-col bg-shell-0 relative
             pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
      @mousemove="onShellPointerMove"
      @pointermove="onShellPointerMove"
  >
    <!-- 10px Left-Edge Hover Trigger Zone (active when sidebar is collapsed and not peeking, MAR-1337 AC5) -->
    <div
      data-sidebar-edge-zone
      class="hidden sm:block fixed top-0 bottom-8 left-0 w-2.5 z-45"
      :class="isSidebarCollapsed && !isSidebarPeeking ? 'pointer-events-auto' : 'pointer-events-none'"
      @mouseenter="startSidebarPeek"
      @mousemove="onShellPointerMove"
    />

    <!-- The Activity Center panel is a sibling of the bar, not a child: on mobile it is a full-screen
         sheet, and nesting it inside a 44px-tall band would trap it in that stacking context. -->
    <!-- The only surface that reports activity in full. The push half that used to sit here — a toast
         stack rendering the same tasks (#274) — was removed in #459: activity is reported here, full
         stop. The bell still carries progress and failure on its own (spinner, and a dot while
         anything is in attention — `ActivityButton.vue`); what no longer announces itself anywhere is
         a task that settles successfully. That is the accepted trade, not a gap to fill with a banner
         or a badge. -->
    <ActivityPanel/>

    <!-- The frame row: the one rail, and the sheet. -->
    <div class="flex-1 min-h-0 flex bg-sidebar">
      <IconRail/>

      <!-- ── THE PAPER PLANE / MAIN CANVAS CARD (MAR-1337 Frame-in-Frame) ───────────────────
           Inset all-around Main Canvas Card (`sm:mt-2 sm:mr-2 sm:rounded-[10px] sm:border sm:border-sidebar-border sm:app-plane`):
           - `sm:ml-0` when the left sidebar is Docked (`!isSidebarCollapsed`), because the docked sidebar
             supplies the left frame.
           - `sm:ml-2` (`8px`) when the left sidebar is Collapsed (`isSidebarCollapsed`), giving a symmetric
             frame bezel around the sheet.
           - Transitions are disabled (`transition-none`) while dragging the sidebar resize handle (`isSidebarResizing`)
             for zero-lag real-time resizing.
           - Below `sm` the sheet stays full-bleed. -->
      <div
          data-main-canvas-card
          class="relative flex-1 min-w-0 flex flex-col overflow-hidden bg-background
                 sm:mt-2 sm:mr-2 sm:rounded-[10px] sm:border sm:border-sidebar-border
                 sm:app-plane"
          :class="[
            isSidebarCollapsed ? 'sm:ml-2' : 'sm:ml-0',
            isSidebarResizing ? 'transition-none' : 'sm:transition-[margin-left] sm:duration-180 sm:ease-[cubic-bezier(0.22,1,0.36,1)]',
          ]"
      >
        <!-- Subtle backdrop dimming on the Main Canvas Card during Floating Peek (MAR-1337 AC5). -->
        <div
          data-canvas-peek-backdrop
          :data-visible="isSidebarCollapsed && isSidebarPeeking ? 'true' : 'false'"
          class="hidden sm:block absolute inset-0 z-35 pointer-events-none bg-foreground/8 dark:bg-background/40 transition-opacity duration-150"
          :class="isSidebarCollapsed && isSidebarPeeking ? 'opacity-100' : 'opacity-0'"
        />

        <!-- The bar spans the whole sheet and is PAPER, not frame (v0.2 §07). -->
        <AppBar/>

    <!-- Service banners span the full width of the sheet, directly under the bar — they are
         app-wide statements, not something belonging to the content column. -->
    <div
        v-if="degraded && !bannerDismissed"
        class="shrink-0 flex items-center justify-center gap-2 bg-warning/10 border-b border-warning-ink/30 px-4 py-1.5 text-xs text-warning-ink"
    >
      <WifiOff class="h-3.5 w-3.5 shrink-0"/>
      <span>{{ t('service.degraded') }}</span>
      <button
          type="button"
          class="ml-auto p-0.5 rounded-md hover:bg-warning/15 transition-colors"
          @click="bannerDismissed = true"
      >
        <X class="h-3.5 w-3.5"/>
      </button>
    </div>

    <div
        v-if="usageBlocked"
        class="shrink-0 flex items-center justify-center gap-2 bg-destructive/10 border-b border-destructive/30 px-4 py-1.5 text-xs text-destructive"
    >
      <AlertTriangle class="h-3.5 w-3.5 shrink-0"/>
      <span>
        {{ t(usageLimitMessageKey({category: usageInfo?.category ?? null}), {plan: usageInfo?.planLabel}) }}
        <NuxtLink to="/app/settings/organization" class="underline hover:no-underline">{{
            t('service.viewUsage')
          }}</NuxtLink>
      </span>
      <button
          type="button"
          class="ml-auto p-0.5 rounded-md hover:bg-destructive/15 transition-colors"
          @click="dismissUsageLimit"
      >
        <X class="h-3.5 w-3.5"/>
      </button>
    </div>

    <!-- Org notices (MAR-428) sit with the service banners, and BELOW them: a degraded backend or a
         blown quota is about whether the app works at all, which outranks an announcement from the
         firm. It renders nothing until its own fetch returns, and nothing at all if that fails. -->
    <GuidanceNoticeBanner/>

        <!-- The columns, inside the sheet and below the bar:
             documents | content | chat | context rail.

             ClientOnly wraps only the three client-state columns. The sheet itself, the bar and the
             context rail all server-render — a column that mounts after hydration shifts everything
             beside it sideways on every load. -->
        <div class="flex-1 min-h-0 flex">
          <!-- The WORKING AREA, and what the floating chat docks against (`lightRegionEl`). It is
               `relative` so the popup is positioned against this box rather than the viewport,
               which keeps it below the bar and inside the sheet's rounded inset.

               **It deliberately excludes the context rail.** The rail is a sibling, not a child:
               docking against the whole row would let the chat popup park itself on top of the
               rail, covering the case context at exactly the moment you are asking questions about
               that case. This was frame before MAR-127 and therefore outside the dock box for free;
               now that it is a paper column, the exclusion has to be structural. -->
          <div ref="lightRegionEl" class="relative flex-1 min-w-0 flex">
          <ClientOnly>
          <!-- Documents drawer (toggleable, resizable).
               Below `sm` it is a full-width pane rather than a 288px column, shown only when the tab
               bar has selected it. The width has to be a class and not the old inline `flexBasis`:
               an inline style wins over every `sm:` utility, so the column stayed 288px wide on a
               phone no matter what was declared. -->
          <div
              v-if="docsDrawerOpen"
              class="shrink-0 overflow-hidden w-full sm:w-72"
              :class="[mobilePane === 'docs' ? 'block' : 'hidden', 'sm:block']"
          >
            <FileExplorer/>
          </div>

          <ResizeHandle v-if="docsDrawerOpen" flush class="hidden sm:block" direction="horizontal" :min-size="200"/>

          <!-- Center content: route page -->
          <div
              v-if="contentActive"
              class="flex-1 min-w-0 flex-col"
              :class="[mobilePane === 'content' ? 'flex' : 'hidden', 'sm:flex']"
          >
            <div class="flex-1 min-h-0 overflow-y-auto">
              <slot/>
            </div>
          </div>

          <!-- Chat: fills the centre when there is no page, floats over it when there is (MAR-125).
               `min-w-0` is load-bearing in the centred case: a flex item defaults to
               `min-width: auto`, so without it this column cannot shrink below the widest unbreakable
               thing inside it. `truncate` sets `white-space: nowrap`, so one long chat-scope label
               ("CLIENT › Case") became the floor and pushed the entire column past the viewport.

               ONE ChatPanel, always. The floating and centred presentations are the same instance in
               a differently-styled container — rendering a second panel for the popup would mount a
               second chat, with its own stream, its own draft and its own scroll position. -->
          <div
              v-if="isChatPaneVisible(contentActive, chatPanelOpen, mobilePane) && !(contentActive && chatBlockedFeature)"
              ref="chatPanelEl"
              :class="[chatContainerClass, mobilePane === 'chat' ? 'block' : 'hidden', 'sm:block']"
              :style="chatPanelStyle"
              data-chat-container
              :data-chat-mode="contentActive ? 'floating' : 'centred'"
              :data-chat-corner="chatCorner"
              @pointerdown="onChatPointerDown"
              @pointermove="onChatPointerMove"
              @pointerup="onChatPointerUp"
              @pointercancel="onChatPointerUp"
              @click.capture="onChatClickCapture"
          >
            <!-- `compact` follows the DESKTOP layout, not the mobile pane: below `sm` chat has the
                 whole screen even when a page exists, so the cramped variant would be wrong there. -->
            <PlanFeatureNotice v-if="chatBlockedFeature" :feature="chatBlockedFeature"/>
            <ChatPanel v-else :compact="contentActive"/>
          </div>

          <!-- The FAB that brings the popup back. Desktop only: below `sm` chat is a tab-bar pane,
               and a floating button over a phone screen would cover the content it sits on. -->
          <button
              v-if="contentActive && !chatPanelOpen && !chatBlockedFeature"
              ref="chatFabEl"
              data-chat-drag-handle
              data-chat-fab
              :data-chat-corner="chatCorner"
              type="button"
              class="hidden sm:flex absolute z-40 h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              :class="[
                chatCornerClass,
                fabDragging ? 'cursor-grabbing' : 'cursor-pointer transition-[top,left,right,bottom,background-color] duration-150',
              ]"
              :style="fabDragStyle"
              :title="t('webChat.openChat')"
              :aria-label="t('webChat.openChat')"
              @pointerdown="onFabPointerDown"
              @pointermove="onFabPointerMove"
              @pointerup="onFabPointerUp"
              @pointercancel="onFabPointerUp"
              @click="onFabClick"
          >
            <MessageSquare class="h-5 w-5"/>
          </button>

          <template #fallback>
            <!-- The sheet already server-renders with its ground, corner and shadow, so this only
                 has to hold the columns' space until they mount. -->
            <div class="flex-1 flex items-center justify-center">
              <div class="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin"/>
            </div>
          </template>
          </ClientOnly>
          </div>

          <!-- The context rail (MAR-115) — now a COLUMN INSIDE THE SHEET, not a rail on the frame
               (Sistema Portafolio v0.2 §07). Everything it carries — client names, NITs,
               counterparties, filenames, case history — belongs to the client, and by §01 that is
               paper. It sits on the same `background` as the working column, divided from it by a
               1px `border` hairline and nothing else (MAR-128) — it was the recessed `muted` tier
               with the hairline on top, which is two separators doing one job.

               It stays outside ClientOnly for the reason it always did: a column that mounts only
               after hydration shifts the working area sideways on every load. It server-renders at
               `w-0` (the scope is global until the remembered one is restored) and widens itself
               along its own transition — which is also why it is never `v-if`'d away when unscoped. -->
          <ContextRail/>
        </div>
      </div>

      <!-- Hidden slot for /app to keep NuxtPage in the tree -->
      <div v-if="isHome" class="hidden">
        <slot/>
      </div>
    </div>

    <!-- Persistent Bottom Frame Bar on desktop (MAR-1337 AC1): 32px (`h-8`) bezel beneath the inset
         Main Canvas Card housing right-aligned utility controls (`▷ Agent` and `◨` ContextRail toggle). -->
    <footer
      data-bottom-frame-bar
      class="hidden sm:flex h-8 shrink-0 items-center justify-end gap-1.5 px-2.5 bg-sidebar text-sidebar-foreground select-none"
    >
      <button
        type="button"
        data-bottom-agent-toggle
        class="inline-flex h-6 items-center gap-1.5 rounded-md border px-2 text-[11.5px] font-medium transition-colors"
        :class="
          chatPanelOpen
            ? 'border-sidebar-border bg-sidebar-hover text-sidebar-foreground'
            : 'border-transparent text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-foreground'
        "
        :title="t('nav.chat')"
        :aria-label="t('nav.chat')"
        :aria-expanded="chatPanelOpen"
        @click="toggleChatPanel"
      >
        <span>▷ Agent</span>
        <span
          v-if="activity.hasWork"
          class="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-notification px-1 text-[10px] font-bold leading-none text-notification-foreground"
        >{{ activity.summary.count }}</span>
      </button>

      <button
        v-if="contextRailAvailable"
        type="button"
        data-bottom-context-toggle
        class="inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors"
        :class="
          contextRailExpanded
            ? 'border-sidebar-border bg-sidebar-hover text-sidebar-foreground'
            : 'border-transparent text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-foreground'
        "
        :title="contextRailExpanded ? t('contextRail.collapse') : t('contextRail.expand')"
        :aria-label="contextRailExpanded ? t('contextRail.collapse') : t('contextRail.expand')"
        :aria-expanded="contextRailExpanded"
        @click="toggleContextRail"
      >
        <component :is="contextRailExpanded ? PanelRightClose : PanelRight" class="h-3.5 w-3.5" />
      </button>
    </footer>

    <!-- The mobile navigation surface (MAR-123), replacing the rail below `sm`. A sibling of the
         frame row rather than a child, so it sits on the frame ground the same way the rail does and
         is never clipped by the sheet's `overflow-hidden`. -->
    <MobileTabBar/>

    <!-- The ONE upload-target picker (#83). Mounted here so every entry point in the shell — the
         rail, the file explorer, the finder, a folder drop — opens the same dialog through the same
         store instead of growing a picker each. (The window drop was the fifth; it is gone.) -->
    <UploadTargetDialog/>

  </div>
</template>
